/**
 * installPrompt — beslutslogik för "Lägg till på hemskärmen"-prompten.
 *
 * Ren logik utan DOM så den kan enhetstestas: när prompten får visas styrs av
 * besöksräknare (tidigast andra besöket), snooze ("Inte nu", 14 dagar) och
 * permanent avfärdande (X). Allt persisteras i localStorage under en nyckel.
 */

const STORAGE_KEY = 'ziplink-access-install-prompt';
const SESSION_VISIT_KEY = 'ziplink-access-install-prompt-visit';

export const MIN_VISITS = 2;
export const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14 dagar

export interface InstallPromptState {
  visits: number;
  dismissed: boolean;
  snoozedUntil: number | null;
}

const DEFAULT_STATE: InstallPromptState = {
  visits: 0,
  dismissed: false,
  snoozedUntil: null,
};

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readState(storage: KeyValueStorage): InstallPromptState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<InstallPromptState>;
    return {
      visits: typeof parsed.visits === 'number' ? parsed.visits : 0,
      dismissed: parsed.dismissed === true,
      snoozedUntil: typeof parsed.snoozedUntil === 'number' ? parsed.snoozedUntil : null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function writeState(storage: KeyValueStorage, state: InstallPromptState): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function shouldShowInstallPrompt(state: InstallPromptState, now: number): boolean {
  if (state.dismissed) return false;
  if (state.snoozedUntil !== null && now < state.snoozedUntil) return false;
  return state.visits >= MIN_VISITS;
}

/**
 * Räknar upp besöksräknaren, max en gång per webbläsarsession
 * (sessionStorage-vakt så att reloads inom samma session inte dubbelräknas).
 */
export function registerVisit(storage: KeyValueStorage, session: KeyValueStorage): InstallPromptState {
  const state = readState(storage);
  if (session.getItem(SESSION_VISIT_KEY)) return state;
  session.setItem(SESSION_VISIT_KEY, '1');
  const next = { ...state, visits: state.visits + 1 };
  writeState(storage, next);
  return next;
}

/** "Inte nu" — göm prompten i SNOOZE_MS och fråga igen därefter. */
export function snoozeInstallPrompt(storage: KeyValueStorage, now: number): void {
  writeState(storage, { ...readState(storage), snoozedUntil: now + SNOOZE_MS });
}

/** X eller lyckad installation — visa aldrig prompten igen. */
export function dismissInstallPrompt(storage: KeyValueStorage): void {
  writeState(storage, { ...readState(storage), dismissed: true });
}
